import { convexClient, crossDomainClient } from "@convex-dev/better-auth/client/plugins";
import { decodeDesktopAuthSessionCookie } from "@papersflow/contracts/desktop-auth-session";
import { anonymousClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

export const WORKSPACE_AUTH_STORAGE_PREFIX = "papersflow-auth";
export const WORKSPACE_AUTH_COOKIE_STORAGE_KEY = `${WORKSPACE_AUTH_STORAGE_PREFIX}_cookie`;
const workspaceAuthSessionCacheKey = `${WORKSPACE_AUTH_STORAGE_PREFIX}_session_data`;

export interface WorkspaceAuthCookieStorage {
  readonly getItem: (key: string) => string | null;
  readonly setItem: (key: string, value: string) => Promise<void>;
}

export interface WorkspaceAuthClient {
  readonly convex: {
    readonly token: (options?: {
      readonly fetchOptions?: { readonly throw?: boolean };
    }) => Promise<{
      readonly data?: { readonly token?: string | null } | null;
      readonly error?: unknown;
    }>;
  };
  readonly signIn: {
    readonly anonymous: () => Promise<{ readonly error?: unknown }>;
  };
  readonly signOut?: () => Promise<unknown>;
}

export function createDesktopWorkspaceAuthStorage(
  initialCookie: string | null,
  persistCookie: (cookie: string | null) => Promise<void>,
): WorkspaceAuthCookieStorage {
  let cookie = initialCookie === null ? null : decodeDesktopAuthSessionCookie(initialCookie);

  return {
    getItem: (key) => {
      if (key === WORKSPACE_AUTH_COOKIE_STORAGE_KEY) return cookie;
      return null;
    },
    setItem: async (key, value) => {
      if (key === workspaceAuthSessionCacheKey) return;
      if (key !== WORKSPACE_AUTH_COOKIE_STORAGE_KEY) {
        throw new Error("Unsupported workspace authentication storage key.");
      }
      const nextCookie = value === "{}" ? null : decodeDesktopAuthSessionCookie(value);
      await persistCookie(nextCookie);
      cookie = nextCookie;
    },
  };
}

export function createSameOriginWorkspaceAuthClient(baseURL: string): WorkspaceAuthClient {
  return createAuthClient({
    baseURL,
    plugins: [convexClient(), anonymousClient()],
  });
}

export function createDesktopWorkspaceAuthClient(
  baseURL: string,
  storage: WorkspaceAuthCookieStorage,
): WorkspaceAuthClient {
  return createAuthClient({
    baseURL,
    plugins: [
      convexClient(),
      anonymousClient(),
      crossDomainClient({
        storage,
        storagePrefix: WORKSPACE_AUTH_STORAGE_PREFIX,
        disableCache: true,
      }),
    ],
  });
}
